import { Controller } from "@hotwired/stimulus"

/**
 * Datetime Snap Controller
 *
 * Purpose: Client-side datetime-local formatting
 *
 * Behavior:
 * - Listens for blur/change events on datetime-local inputs
 * - Removes seconds and milliseconds from user input
 * - Ensures compatibility with mobile Safari datetime pickers
 *
 * Usage:
 * <input type="datetime-local" data-controller="datetime-snap">
 */
export default class extends Controller {
  connect() {
    // Check if flatpickr is active on this element
    // This controller acts as a fallback for native datetime inputs
    this.hasFlatpickr = this.element.closest('[data-controller*="flatpickr"]') !== null

    if (!this.hasFlatpickr) {
      // Only attach listeners if flatpickr is not active
      this.element.addEventListener('blur', this.formatDateTime.bind(this))
      this.element.addEventListener('change', this.formatDateTime.bind(this))
    }
  }

  disconnect() {
    if (!this.hasFlatpickr) {
      this.element.removeEventListener('blur', this.formatDateTime.bind(this))
      this.element.removeEventListener('change', this.formatDateTime.bind(this))
    }
  }

  formatDateTime(event) {
    const input = event.target
    const value = input.value

    // Skip if empty
    if (!value) return

    try {
      // Parse the datetime-local value (YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS)
      const dateTime = new Date(value)

      // Validate parsed date
      if (isNaN(dateTime.getTime())) {
        console.warn('Invalid datetime value:', value)
        return
      }

      // Remove seconds and milliseconds only
      dateTime.setSeconds(0, 0)

      // Format as datetime-local string (YYYY-MM-DDTHH:MM)
      const formattedValue = this.formatDateTimeLocal(dateTime)

      // Update input value if it changed
      if (input.value !== formattedValue) {
        input.value = formattedValue

        // Dispatch events to notify other controllers
        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.dispatchEvent(new Event('change', { bubbles: true }))
      }
    } catch (error) {
      console.error('Error formatting datetime:', error)
    }
  }

  /**
   * Format Date object as datetime-local string (YYYY-MM-DDTHH:MM)
   * @param {Date} date - Date object to format
   * @returns {string} Formatted string
   */
  formatDateTimeLocal(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}`
  }
}
